import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * @typedef {Object} SEOProps
 * @property {string} [title]
 * @property {string} [description]
 * @property {string} [keywords]
 * @property {string} [image]
 * @property {string} [type]
 * @property {boolean} [article]
 * @property {string} [publishedTime]
 * @property {string} [modifiedTime]
 * @property {string} [author]
 * @property {string | string[]} [tags]
 * @property {string} [canonical]
 */

/**
 * SEO Component for managing meta tags dynamically
 * Usage: <SEO title="Page Title" description="Page description" />
 * All props are optional
 *
 * @param {SEOProps} [props]
 * @returns {null}
 */
const SEO = (props = {}) => {
  /** @type {SEOProps} */
  const propsWithDefaults = props || {};
  const {
    title,
    description,
    keywords,
    image,
    type = 'website',
    article = false,
    publishedTime,
    modifiedTime,
    author,
    tags,
    canonical
  } = propsWithDefaults;
  const location = useLocation();
  
  // Default values
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const currentUrl = `${siteUrl}${location.pathname}`;
  const siteName = 'Tafsir Art';
  const defaultTitle = 'Tafsir Art - The Art of Interpreting the Sacred';
  const defaultDescription = 'Tafsir Art unites the exegetical depth of the Quran with the universal language of art, materializing divine wisdom into pieces that invite daily reflection.';
  const defaultKeywords = 'Tafsir Art, Tafsir, Quranic art, Islamic art, Arabic calligraphy, Tadabbur, Quran interpretation, Islamic prints, Islamic wall art, visual dawah, Hikma, Quran art';
  const defaultImage = siteUrl ? `${siteUrl}/logo_white.jpg` : 'https://tafsirart.com/logo_white.jpg';
  
  const pageTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;
  const pageImage = image || defaultImage;
  const pageCanonical = canonical || currentUrl;
  
  useEffect(() => {
    // Set document title
    document.title = pageTitle;
    
    // Helper function to set meta tag
    const setMetaTag = (name, content, isProperty = false) => {
      if (!content) return;
      
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };
    
    // Helper function to set link tag
    const setLinkTag = (rel, href) => {
      if (!href) return;
      
      let element = document.querySelector(`link[rel="${rel}"]`);
      
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      
      element.setAttribute('href', href);
    };
    
    // Standard meta tags
    setMetaTag('description', pageDescription);
    setMetaTag('keywords', pageKeywords);
    setMetaTag('author', author || siteName);
    
    // Open Graph tags
    setMetaTag('og:title', pageTitle, true);
    setMetaTag('og:description', pageDescription, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:url', currentUrl, true);
    setMetaTag('og:image', pageImage, true);
    setMetaTag('og:site_name', siteName, true);
    setMetaTag('og:locale', 'en_US', true);
    
    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', pageTitle);
    setMetaTag('twitter:description', pageDescription);
    setMetaTag('twitter:image', pageImage);
    
    // Article specific tags
    if (article) {
      setMetaTag('article:published_time', publishedTime, true);
      setMetaTag('article:modified_time', modifiedTime, true);
      setMetaTag('article:author', author, true);
 
      // Reset article tags to prevent duplicates
      const existingTagElements = document.querySelectorAll('meta[property="article:tag"]');
      existingTagElements.forEach(tagElement => {
        tagElement.parentElement?.removeChild(tagElement);
      });
 
      if (tags) {
        const tagValues = Array.isArray(tags) ? tags : [tags];
        tagValues.forEach(tag => {
          const tagElement = document.createElement('meta');
          tagElement.setAttribute('property', 'article:tag');
          tagElement.setAttribute('content', tag);
          document.head.appendChild(tagElement);
        });
      }
    }
    
    // Canonical URL
    setLinkTag('canonical', pageCanonical);
    
    // Additional SEO tags
    setMetaTag('robots', 'index, follow');
    setMetaTag('googlebot', 'index, follow');
    setMetaTag('revisit-after', '7 days');
    setMetaTag('language', 'English');
    
  }, [
    pageTitle, 
    pageDescription, 
    pageKeywords, 
    pageImage, 
    currentUrl, 
    pageCanonical,
    type,
    article,
    publishedTime,
    modifiedTime,
    author,
    tags
  ]);
  
  return null;
};
 
export default SEO;
 
// Pre-defined SEO configurations for common pages
/**
 * @type {Record<string, {title: string, description: string, keywords: string}>}
 */
export const SEOConfigs = {
  home: {
    title: 'The Art of Interpreting the Sacred | Islamic Calligraphy & Art',
    description: 'Tafsir Art unites the exegetical depth of the Quran with the universal language of art. Explore contemporary Islamic art prints materializing divine wisdom.',
    keywords: 'Tafsir Art, Tafsir, Quranic art, Islamic art, Arabic calligraphy, Tadabbur, Quran interpretation, Islamic prints, Islamic wall art, visual dawah'
  },
  gallery: {
    title: 'Art Gallery | Contemporary Calligraphy & Prints',
    description: 'Browse Tafsir Art\'s collection of Islamic calligraphy. Unique visual signs and art prints inspired by Quranic verses, inviting deep meditation.',
    keywords: 'Islamic art gallery, Arabic calligraphy collection, Quranic art prints, spiritual art, Tafsir Art gallery'
  },
  store: {
    title: 'Islamic Art Store | Premium Canvas & Calligraphy Prints',
    description: 'Shop premium Tafsir Art prints. High-quality Arabic calligraphy and contemporary Islamic art pieces designed to invite daily reflection.',
    keywords: 'buy Islamic art, Tafsir Art store, Islamic prints store, Arabic calligraphy shop, Islamic wall art store'
  },
  blog: {
    title: 'Blog | Insights on Quranic Exegesis & Islamic Art',
    description: 'Read articles exploring the exegetical depth of the Quran, the tradition of calligraphy, and the role of art in spiritual contemplation.',
    keywords: 'Islamic art blog, Quranic exegesis, Tadabbur, Arabic calligraphy blog, Islamic art history'
  },
  about: {
    title: 'About Us | The Origin & Foundations of Tafsir Art',
    description: 'Discover the origin, mission, and theological foundations of Tafsir Art: uniting the depth of Tafsir with the universal language of art.',
    keywords: 'about Tafsir Art, Tadabbur, Hikma, Dawah, Quranic calligraphy origin, visual sign, observation and reflection'
  },
  contact: {
    title: 'Contact Us | Custom Commissions & Collaborations',
    description: 'Reach out to Tafsir Art for custom commissions, inquiries about our spiritual artworks, or collaboration opportunities in visual Dawah.',
    keywords: 'contact Tafsir Art, custom calligraphy, Islamic art commissions, contact artist'
  }
};
